import React from "react";
import { Bar } from "react-chartjs-2";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import makeStyles from "@mui/styles/makeStyles";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Title,
  Tooltip,
  PointElement,
  LineElement,
} from "chart.js";

import CenterFlexBox from "components/CenterFlexBox";
import { getBatchRecentTemplateScores } from "services/analyticsV2";
import { InterviewVariantTypes } from "components/Analytics/constant";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  PointElement,
  LineElement
);

const useStyles = makeStyles((theme) => ({
  tooltip: {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #F0F0F0",
    boxShadow: "5px 3px 13px 0px #4747491A",
    padding: theme.spacing(1,4),
    transform: "all 200ms",
    pointerEvents: "none",
    width: 300,
  },
  tooltipHeading: {
    ...theme.typography['body01-bold'],
    textTransform: "capitalize",
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
  tooltipData: {
    padding: theme.spacing(1,5),
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tooltipDataValue: {
    ...theme.typography['body01-semiBold'],
  },
  tooltipDataLabel: {
    ...theme.typography['body01-medium'],
    color: theme.palette.neutral['clr-800']
  },
  changeChip: {
    width: 60,
    borderRadius: 12,
    padding: theme.spacing(1, 2),
    ...theme.typography['body02-bold'],
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  increment: {
    backgroundColor: theme.palette.success['clr-100'],
    color: theme.palette.success['clr-700'],
  },
  decrement: {
    backgroundColor: theme.palette.danger['clr-100'],
    color: theme.palette.danger['clr-700'],
  },
  performanceLink: {
    color: theme.palette.shades['clr-white-900'],
    background: theme.palette.primary.main,
    borderRadius: 12,
    fontWeight: 400,
    fontSize: '10px',
    padding: theme.spacing(1,2),
  },
  emptyState: {
    marginBottom: theme.spacing(2.5)
  },
  graphContainer: {
    width: "100%",
    background: theme.palette.shades['clr-white-900'],
    border: "1px solid lightgrey",
    borderRadius: '8px',
    padding: theme.spacing(2),
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  }
}));

const RecentTemplatesAnalyticsSkeleton = ({ interviewVariantType }) => {
  const classes = useStyles();
  
  return (
    <>
      <CenterFlexBox justifyContent="space-between" mb='20px'>
        <Box display='flex' flexDirection='column' gap='4px' width='80%'>
         <Typography variant='h4-medium'>
                    Batch highlights
                </Typography>
                <Typography variant='h6-medium' color='neutral.clr-600'>
                     Track students activities across the batch
                </Typography>
        </Box>
      </CenterFlexBox>

      <Box className={classes.graphContainer}>
        
        <Box display="flex" alignItems="center">
          <Box sx={{ transform: "rotate(-90deg)", width: 50 }}> 
            <Skeleton variant="text" width={40} height={20} />
          </Box>
          <Box style={{ width: "100%", minHeight: 300 }}>
            <Skeleton variant="rectangular" width="100%" height={300} />
          </Box>
        </Box>
        
        <Box textAlign="center" mt={1}>
          <Skeleton variant="text" width={80} height={20} sx={{ margin: '0 auto' }} />
        </Box>
      </Box>
    </>
  );
};

const RecentTemplatesAnalyticsEmptyState = ({ interviewVariantType }) => {
  const classes = useStyles();
  
  return (
    <>
      <CenterFlexBox justifyContent="space-between" mb='20px'>
        <Box display='flex' flexDirection='column' gap='4px' width='80%'>
          <Typography variant="h4-medium">
            Recent {InterviewVariantLabels[interviewVariantType] || 'Analytics'}
          </Typography>
          <Typography variant="h6-medium" color='neutral.clr-600'>
            The graph tooltip shows changes in values for each {InterviewVariantLabels[interviewVariantType]?.toLowerCase() || 'template'}, comparing them with the cumulative average, 
            considering all prior templates
          </Typography>
        </Box>
      </CenterFlexBox>

      <Box className={classes.graphContainer}>
        <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 300 }}>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <Box mb={2}>
              <img
                src='https://assets.languify.in/images/no_data.png'
                alt='no data'
                width={151}
                height={97}
              />
            </Box>
            <Typography variant="h6-medium">No data available</Typography>
          </Box>
        </Box>
        <Box textAlign="center" mt={1}>
          <Typography variant="body02-semiBold">
            {InterviewVariantLabels[interviewVariantType] || 'Templates'}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

const AssessmentTags = {
  all : 'all templates',
  contest: 'contest',
  mock: 'mock',
}

const InterviewVariantLabels = {
  verbal_mock: 'Next Round Mock',
  coding_mock: 'Coding Mock',
  verbal_iw: 'Next Round Interview',
  coding_iw: 'Coding Interview',
  assessment: 'Assessment'
}

function RecentTemplatesAnalytics({ batchId, interviewVariantType = InterviewVariantTypes.VERBAL_MOCK }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id: urlId } = useParams();
  const id = batchId || urlId;

  const [templateType, setTemplateType] = React.useState("all");
  const [templatesAnalytics, setTemplatesAnalytics] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await getBatchRecentTemplateScores(id, interviewVariantType);

        let data = response|| [];

        setTemplatesAnalytics(data);
        setError(null);
      } catch (error) {
        setError(error.message);
        setTemplatesAnalytics([]);
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchTemplates();
  }, [id, interviewVariantType, templateType]);

  const getGraphToolTip = (context) => {
    const { chart } = context;
    let tooltipEl = document.getElementById("chartjs-tooltip");

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "chartjs-tooltip";
      tooltipEl.classList.add(classes.tooltip);
      chart.canvas.style.position = "relative";
      chart.canvas.parentNode.appendChild(tooltipEl);
    }
    tooltipEl.innerHTML = "";

    const tooltipModel = context.tooltip;
    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    tooltipEl.classList.remove("above", "below", "no-transform");
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
      tooltipEl.classList.add("no-transform");
    }

    const dataIndex = tooltipModel.dataPoints?.[0].dataIndex;
    const currData = templatesAnalytics[dataIndex] || {};

    const heading = document.createElement("p");
    heading.className = classes.tooltipHeading;
    heading.innerHTML = `${currData.templateName || ''}`;
    tooltipEl.appendChild(heading);

    const dataContainer = document.createElement("div");
    dataContainer.style.width = "100%";
    let tooltipContent = "";

    if (interviewVariantType === 'coding_mock') {
      tooltipContent += `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Coding</div>
        <div class='${classes.tooltipDataValue}'>${currData.coding?.overall ?? 0}/5</div>
      </div>`;
      tooltipContent += `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Core Concepts</div>
        <div class='${classes.tooltipDataValue}'>${currData.coreConcepts?.overall ?? 0}/5</div>
      </div>`;
      tooltipContent += `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Critical Thinking</div>
        <div class='${classes.tooltipDataValue}'>${currData.criticalThinking?.overall ?? 0}/5</div>
      </div>`;
      tooltipContent += `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Communication</div>
        <div class='${classes.tooltipDataValue}'>${currData.content?.overall ?? 0}/5</div>
      </div>`;
    } else {
      tooltipContent += `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Speech</div>
        <div class='${classes.tooltipDataValue}'>${currData.communicationOverall ?? 0}</div>
      </div>`;
      tooltipContent += `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Content</div>
        <div class='${classes.tooltipDataValue}'>${currData.contentOverall ?? 0}</div>
      </div>`;
    }
    if (!tooltipContent) {
      tooltipContent = `
      <div class='${classes.emptyState}'>
        <div>
          <img src='https://assets.languify.in/images/noAttempts.svg' alt='no attempts yet'/>
        </div>
        <div> No Attempts yet</div>
      </div>`;
    }
    dataContainer.innerHTML = tooltipContent;
    tooltipEl.appendChild(dataContainer);

    const { offsetLeft: positionX, offsetTop: positionY } = context.chart.canvas;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.zIndex = 10000000000;
    tooltipEl.style.left = positionX + tooltipModel.caretX - 250 + "px";
    tooltipEl.style.top = positionY + tooltipModel.caretY - 140 + "px";
  };


  const GraphOptions = {
    responsive: true,
    aspectRatio: 5,
    maintainAspectRatio: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      title: {
        display: false,
        text: "Performance",
      },
      tooltip: {
        enabled: false,
        position: "average",
        external: getGraphToolTip,
      },
    },
    onClick: (e) => {
      document.getElementById("chartjs-tooltip").style.opacity = 0;

      const index = e?.chart?.tooltip?.dataPoints?.[0]?.dataIndex;
      const tid = templatesAnalytics[index]._id;

      navigate(`/assessments/${tid}/performance`);
    },
    scales: { 
      yAxis: { min: 0, max: 100 },
      xAxis: {
        display: true,
        ticks: {
          callback: function(value, index, ticks) {
            const label = this.getLabelForValue(value);
            return label.length > 10 ? label.substr(0, 10) + '...' : label;
          },
          maxRotation: 45,
          minRotation: 0,
        }
      }
    },
  };

  const datasets = React.useMemo(() => {
    const _datasets = [];
    _datasets.push({
      label: "Speech",
      data: templatesAnalytics.map((qs) => (qs.communicationOverall ?? 0)),
      backgroundColor: "#B8ACF6",
      hoverBackgroundColor: '#8270DB',
    });
    _datasets.push({
      label: "Content",
      data: templatesAnalytics.map((qs) => (qs.contentOverall ?? 0)),
      backgroundColor: "#9DE4F2",
      hoverBackgroundColor: '#52B8CC'
    });
    return _datasets;
  }, [templatesAnalytics]);

  const labels = React.useMemo(() => {
    const _labels = templatesAnalytics.map((x, i) => {
      return x.settings?.name || x.templateName || '';
    });
    if (_labels.length < 10) {
      for (let x = _labels.length; x < 10; x++) _labels.push("");
    }
    return _labels;
  }, [templatesAnalytics]);

  const limitedTemplatesAnalytics = templatesAnalytics.slice(0, 6);

  let customLabels = labels;
  let customDatasets = datasets;
  let customGraphOptions = { ...GraphOptions };

  if (interviewVariantType === 'coding_mock') {
    customLabels = limitedTemplatesAnalytics.map((x, i) => {
      return x.settings?.name || x.templateName || '';
    });
    if (customLabels.length < 6) {
      for (let x = customLabels.length; x < 6; x++) customLabels.push("");
    }
    customDatasets = [];
    customDatasets.push({
      label: 'Coding',
      data: limitedTemplatesAnalytics.map((qs) => qs.coding?.overall ?? 0),
      backgroundColor: '#6E5DC6',
      hoverBackgroundColor: '#6E5DC6',
    });
    customDatasets.push({
      label: 'Critical Thinking',
      data: limitedTemplatesAnalytics.map((qs) => qs.criticalThinking?.overall ?? 0),
      backgroundColor: '#1151A6',
      hoverBackgroundColor: '#1151A6',
    });
    customDatasets.push({
      label: 'Core Concepts',
      data: limitedTemplatesAnalytics.map((qs) => qs.coreConcepts?.overall ?? 0),
      backgroundColor: '#52B8CC',
      hoverBackgroundColor: '#52B8CC',
    });
    customDatasets.push({
      label: 'Communication',
      data: limitedTemplatesAnalytics.map((qs) => qs.content?.overall ?? 0),
      backgroundColor: '#737DA8',
      hoverBackgroundColor: '#737DA8',
    });
    customGraphOptions = {
      ...GraphOptions,
      scales: {
        ...GraphOptions.scales,
        yAxis: { min: 0, max: 5 }
      }
    };
  } else {
    customLabels = limitedTemplatesAnalytics.map((x, i) => {
      return x.settings?.name || x.templateName || '';
    });
    if (customLabels.length < 6) {
      for (let x = customLabels.length; x < 6; x++) customLabels.push("");
    }
    customDatasets = [
      {
        label: "Speech",
        data: limitedTemplatesAnalytics.map((qs) => (qs.communicationOverall ?? 0)),
        backgroundColor: "#B8ACF6",
        hoverBackgroundColor: '#8270DB',
      },
      {
        label: "Content",
        data: limitedTemplatesAnalytics.map((qs) => (qs.contentOverall ?? 0)),
        backgroundColor: "#9DE4F2",
        hoverBackgroundColor: '#52B8CC'
      }
    ];
  }


  if (loading) {
    return <RecentTemplatesAnalyticsSkeleton interviewVariantType={interviewVariantType} />;
  }
  if (error) {
    return <RecentTemplatesAnalyticsEmptyState interviewVariantType={interviewVariantType} />;
  }

  return (
    <>
             <CenterFlexBox justifyContent="space-between" mb='20px'>
         <Box display='flex' flexDirection='column' gap='4px' width='80%'>
           <Typography variant="h4-medium">
             Recent {InterviewVariantLabels[interviewVariantType] || 'Analytics'}
           </Typography>
           <Typography variant="h6-medium" color='neutral.clr-600'>
             The graph tooltip shows changes in values for each {InterviewVariantLabels[interviewVariantType]?.toLowerCase() || 'template'}, comparing them with the cumulative average, 
             considering all prior&nbsp;
             {AssessmentTags[templateType]}
           </Typography>
         </Box>
       </CenterFlexBox>

      <Box className={classes.graphContainer}>
        <Box display="flex" alignItems="center">
          <Typography
            variant="body02-semiBold"
            style={{ transform: "rotate(-90deg)" }}
          >
            Score
          </Typography>
          <div style={{ width: "100%", minHeight: 300 }}>
            <Bar
              options={{
                ...customGraphOptions,
                maintainAspectRatio: false, 
                responsive: true, 
              }}
              data={{ labels: customLabels, datasets: customDatasets }}
            />
          </div>
        </Box>
                 <div>
           <Typography variant="body02-semiBold" mt={1}>
             {InterviewVariantLabels[interviewVariantType] || 'Templates'}
           </Typography>
         </div>
      </Box>
    </>
  );
}

export default RecentTemplatesAnalytics;
